import { AuthErrorCodes, type AuthenticationError } from '@owner/auth'
import { AxiosHeaders, type AxiosError } from 'axios'

import { FeatureFlags } from '@/plugins/featureFlags'

import { getActiveIframeSession, isSafeParentOrigin } from './iframeSession'

/** The parent owns delegated cookies; recovering here would strand an embedded login screen. */
export function reportIframeCookieFailure(
  error: AxiosError<{ details?: { logout?: boolean; type?: string } }>
): boolean {
  const status = error.status ?? error.response?.status
  const details = error.response?.data?.details
  if (
    !(status === 401 && details?.logout) &&
    !(status === 403 && details?.type === 'invalid-csrf-access-token')
  )
    return false
  return reportInvalidDelegatedCookie(
    AxiosHeaders.from(error.config?.headers).get('X-CSRFToken')
  )
}

export function reportIframeAuthorizerFailure(
  error: Pick<AuthenticationError, 'statusCode' | 'errorCode'>,
  csrfToken: string | null | undefined
): boolean {
  if (
    error.statusCode !== 401 &&
    !(
      error.statusCode === 403 && error.errorCode === AuthErrorCodes.CsrfInvalid
    )
  )
    return false
  return reportInvalidDelegatedCookie(csrfToken)
}

function reportInvalidDelegatedCookie(csrfToken: unknown): boolean {
  if (!FeatureFlags.isEnabled('hermes.hestiaCookieBasedLogin')) return false
  const session = getActiveIframeSession()
  if (
    !session?.csrfToken ||
    !session.handlesCookieInvalidation ||
    !isSafeParentOrigin(session.parentOrigin)
  )
    return false
  if (csrfToken === session.csrfToken) {
    window.parent.postMessage(
      { csrfToken: session.csrfToken, type: 'HERMES_COOKIE_SESSION_INVALID' },
      session.parentOrigin
    )
  }
  return true
}
